import React, {useEffect} from 'react';
import {useSearchParams, Navigate, useNavigate} from 'react-router-dom';
import {Center, Spinner, useToast} from '@chakra-ui/react';
import {captureMessage} from '@sentry/react';
import {
  OAuthPayload,
  OAuthPayloadSchema,
} from '../../shared/api/definitions/integration';
import {useAuthIntegration} from '../../hooks/api/integrations';

function parseOauthPayload(params: string): OAuthPayload | null {
  const {code, state} = JSON.parse(params);
  try {
    const {integrationName, isSandbox, redirectUri, context} =
      JSON.parse(state);

    const oauthPayload = {
      code,
      integrationName,
      isSandbox,
      redirectUri,
      context,
    };
    return OAuthPayloadSchema.parse(oauthPayload);
  } catch {
    return null;
  }
}

const CallbackHandling = () => {
  const toast = useToast();
  const [searchParams] = useSearchParams();
  const params = Object.fromEntries(searchParams);
  const oauthPayload = parseOauthPayload(atob(params['params']));

  if (!oauthPayload) {
    captureMessage('Invalid OAuth payload', {
      extra: {params},
      level: 'error',
    });

    toast({
      title: 'Something went wrong!',
      description: 'Please contact Keyplay for assistance.',
      status: 'error',
      isClosable: true,
    });
    return <Navigate to={'/settings/integrations'} replace />;
  }

  return <_CallbackHandling oauthPayload={oauthPayload} />;
};

const _CallbackHandling = ({oauthPayload}: {oauthPayload: OAuthPayload}) => {
  const toast = useToast();
  const navigate = useNavigate();

  const {mutate: updateOAuth} = useAuthIntegration();

  useEffect(() => {
    updateOAuth(oauthPayload, {
      onSuccess: () => {
        navigate(
          oauthPayload.integrationName === 'linkedin' &&
            oauthPayload.context === 'onboarding'
            ? '/onboarding/linkedin'
            : `/settings/integrations/${oauthPayload.integrationName}`,
          {replace: true}
        );
      },

      onError: (error) => {
        captureMessage('Error updating integration OAuth config', {
          extra: {error, oauthPayload},
          level: 'error',
        });

        toast({
          title: 'Something went wrong!',
          description: 'Please contact Keyplay for assistance.',
          status: 'error',
          isClosable: true,
        });

        navigate(
          oauthPayload.integrationName === 'linkedin' &&
            oauthPayload.context === 'onboarding'
            ? '/onboarding/linkedin'
            : '/settings/integrations',
          {replace: true}
        );
      },
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Center mt={8}>
      <Spinner />
    </Center>
  );
};

export default CallbackHandling;
